import {
    map,
    Observable,
    of,
    catchError
} from "rxjs";

import {
    Injectable, inject
} from "@angular/core";
import {
    BatchProduct,
    BatchProductSearchFilterOptions
} from "@shopliftr/common-js/admin";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse,
    UpdateRequest,
    UpdateResponse
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";

import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ServiceException } from "../../../../exceptions/service-exception";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * Leverages the BatchProduct endpoint to retrieve and manipulate batch products.
 * Implements IFullAccessInternalApiClientService providing full CRUD access including
 * bulk operations for the BatchProduct model via the Internal API v2.
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 */
@Injectable({ providedIn: "root" })
export class BatchProductApiClientService implements IFullAccessInternalApiClientService<BatchProduct> {
    readonly basePath = "batch-product";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves several batch products in a single bulk request.
     *
     * @param ids ids of the batch products to retrieve
     * @returns the bulk response containing one item per id
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<BatchProduct>(id, BatchProduct.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Deletes several batch products in a single bulk request.
     *
     * @param requests delete requests, one per batch product
     * @returns the bulk response
     * @throws ServiceException when no requests are given
     */
    bulkDelete$(requests: Array<DeleteRequest<BatchProduct>>): Observable<BulkResponse> {

        if (!requests?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const requestItems = requests.map((request: DeleteRequest<BatchProduct>) => BulkUtil.createRequestItem(request));
        const bulkRequest = BulkUtil.createBulkRequest(requestItems);
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates several batch products in a single bulk request.
     *
     * @param requests update requests, one per batch product
     * @returns the bulk response
     * @throws ServiceException when no requests are given
     */
    bulkUpdate$(requests: Array<UpdateRequest<BatchProduct>>): Observable<BulkResponse> {

        if (!requests?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const requestItems = requests.map((request: UpdateRequest<BatchProduct>) => BulkUtil.createRequestItem(request));
        const bulkRequest = BulkUtil.createBulkRequest(requestItems);
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Checks that no batch product already uses the given value for a field.
     *
     * @param fieldName search filter field to check (`upc` maps to the `upcs` filter)
     * @param value value to look for
     * @returns `true` when the value is unused (or empty, or the search fails), otherwise `false`
     */
    checkUnique$(fieldName: string, value: any): Observable<boolean> {

        if (value) {

            const options = new BatchProductSearchFilterOptions();

            if (fieldName === "upc") {
                options.upcs = [value];
            }
            else {
                options[fieldName] = value;
            }

            const searchRequest: SearchRequest<BatchProductSearchFilterOptions> = RequestUtil.createSearchRequest(
                ModelAction.ClientSearch,
                BatchProduct.modelName,
                0,
                0,
                options
            );

            return this._internalApiRequestService.search$<BatchProduct, BatchProductSearchFilterOptions>(
                searchRequest,
                this.basePath
            ).pipe(
                map((response: SearchResponse<BatchProduct>) => {

                    return (response.total === 0);
                }),
                catchError(() => {

                    return of(true);
                })
            );
        }
        else {
            return of(true);
        }
    }

    /**
     * Deletes a batch product.
     *
     * @param id UUID of the batch product to delete
     * @param version optional version for optimistic locking
     * @returns the delete response
     */
    delete$(id: string, version?: number): Observable<DeleteResponse> {

        const request = RequestUtil.createDeleteRequest<void>(id, BatchProduct.modelName, ModelAction.ClientDelete, version);

        return this._internalApiRequestService.delete$(request, this.basePath);
    }

    /**
     * Retrieves a batch product.
     *
     * @param id UUID of the batch product to fetch
     * @returns the batch product as stored in Internal API
     */
    get$(id: string): Observable<BatchProduct> {

        const request = RequestUtil.createGetRequest<void>(id, BatchProduct.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<BatchProduct>(request, this.basePath)
            .pipe(map((response: GetResponse<BatchProduct>) => response.payload));
    }

    /**
     * Retrieves every batch product matching the filter options.
     *
     * @param filterOptions search filter options
     * @param sort optional sort
     * @returns all matching batch products
     */
    getAll$(filterOptions: BatchProductSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<BatchProduct>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, BatchProduct.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$<BatchProduct, BatchProductSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Creates a batch product.
     *
     * @param fields batch product to create
     * @returns the created batch product
     */
    create$(fields: BatchProduct): Observable<BatchProduct> {
        const createRequest = RequestUtil.createCreateRequest<BatchProduct, void>(
            ModelAction.ClientCreate, fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Creates several batch products in a single bulk request.
     *
     * @param items create requests, one per batch product
     * @returns the bulk response
     */
    bulkCreate$(items: Array<CreateRequest<BatchProduct, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Searches batch products.
     *
     * @param filterOptions criteria, such as batch id or UPCs, the products must match
     * @param page optional page index
     * @param pageSize optional page size
     * @param sort optional sort
     * @returns the search response
     */
    search$(
        filterOptions: BatchProductSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<BatchProduct>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            BatchProduct.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Updates a batch product.
     *
     * @param id UUID of the batch product to update
     * @param version current version of the batch product
     * @param fields fields to change
     * @param removeFields names of fields to remove
     * @returns the updated batch product
     */
    update$(id: string, version: number, fields?: BatchProduct, removeFields?: Array<string>): Observable<BatchProduct> {

        const request = RequestUtil.createUpdateRequest<BatchProduct, void>(id, ModelAction.ClientUpdate, fields, version, removeFields);
        return this._internalApiRequestService.update$<BatchProduct>(request, this.basePath)
            .pipe(map((response: UpdateResponse<BatchProduct>) => response.payload));
    }

    /**
     * Updates a batch product with the differences between the original and updated models.
     *
     * @param original batch product as last loaded
     * @param updated batch product with the edits applied
     * @returns the updated batch product
     */
    updateWithChanges$(original: BatchProduct, updated: BatchProduct): Observable<BatchProduct> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Returns the total number of batch products belonging to the given batch.
     *
     * Mirrors `DataEntryDealApiClientService.getCount$`: a zero-pageSize `search$` call scoped
     * to the batch, mapped to `SearchResponse.total`, so callers get a live count without
     * paging through results.
     */
    getCount$(batchId: string): Observable<number> {

        const filterOptions = new BatchProductSearchFilterOptions();
        filterOptions.batchId = batchId;
        return this.search$(filterOptions, 0, 0).pipe(map((value: SearchResponse<BatchProduct>) => value.total));
    }
}
